import * as _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import ReactTable from 'react-table';
import type { VoidFunctionComponent } from 'react';
import type {
  CellInfo,
  ComponentPropsGetterR,
  RowInfo,
  SortingRule,
  TableCellRenderer,
  TableProps,
} from 'react-table';

import { RawVulnStatus } from '../../../typings/enums';
import Button from '../generic/Button/Button';
import Icon, { icons } from '../generic/Icon';
import type { CurrentRawVuln } from '../../../shared/model-typings/currentrawvuln';
import type { GroupedRawVulns } from '../../../typings/api';
import type { SetState } from '../../types';

import { getGroupedIndex } from './RawVulnUtils';

type RawVulnsTableProps = {
  rawVulnData: GroupedRawVulns;
  page: number;
  pageSize: number;
  rawVulnsCount: number;
  isLoading: boolean;
  setRawVulnsEditIndex: SetState<string | null>;
  setShowReviewModal: SetState<boolean>;
  handleChangePage: (val: number) => void;
  handleChangePageSize: (val: number) => void;
  handleChangeSorting: (val: SortingRule[]) => void;
};

type RawVulnTableData = {
  projectName: string;
  cve: string;
  fetcher: string;
  status: RawVulnStatus;
  isResolved?: boolean;
  createdAt?: string;
};

type RawVulnTableDataTwo = Pick<CurrentRawVuln, 'projectName' | 'cve' | 'fetcher' | 'status' | 'isResolved' | 'createdAt'>;

type RawVulnColumnMeta = {
  displayName: string;
  columnName?: string;
  inputType?: string;
  show?: boolean;
  sortable?: boolean;
  cellFn?: TableCellRenderer;
};

const reduceRawVulnToTableData = (rawVulns: GroupedRawVulns): RawVulnTableData[] => {
  const fields: Array<keyof RawVulnTableDataTwo> = ['projectName', 'cve', 'fetcher', 'createdAt', 'status', 'isResolved'];
  const newTableData: RawVulnTableData[] = Object.values(rawVulns).map((g: CurrentRawVuln[]) => {
    const rawVuln = _.head(g) as CurrentRawVuln;

    return fields.reduce((acc, field) => {
      // Not a fan of having basically duplicate if blocks, but TS complains if consolidated and it's easy to appease.
      if (field === 'isResolved') {
        acc[field] = rawVuln[field];
      } else if (field === 'cve') {
        acc[field] = rawVuln[field];
      } else if (field === 'status') {
        acc[field] = rawVuln[field] ?? RawVulnStatus.review;
      } else {
        acc[field] = rawVuln[field] ?? '';
      }
      return acc;
    }, {} as RawVulnTableData);
  });

  return newTableData;
};

const RawVulnsTable: VoidFunctionComponent<RawVulnsTableProps> = ({
  rawVulnData,
  page,
  pageSize,
  rawVulnsCount,
  isLoading,
  handleChangePage,
  handleChangePageSize,
  handleChangeSorting,
  setRawVulnsEditIndex,
  setShowReviewModal,
}) => {
  const [tableData, setTableData] = useState<RawVulnTableData[]>([]);

  const numPages = rawVulnsCount ? Math.ceil(rawVulnsCount / pageSize) : 1;

  /*
  *
  * Nasty table setups and functions.
  *
  */
  const EditReviewButton: VoidFunctionComponent<CellInfo> = useCallback((col: CellInfo) => {
    const row = col.row;

    const rawVulnsIndexToEdit = getGroupedIndex(row.cve, row.projectName, row.fetcher);

    const rawVulnGroup = rawVulnData[rawVulnsIndexToEdit] || [];

    const isResolved = rawVulnGroup.some((rawVuln: RawVulnTableData) => rawVuln.isResolved === false);

    const vulnStatuses = rawVulnGroup.map((vuln: RawVulnTableData) => vuln.status);

    let needsReview;
    let buttonContent = <span>Edit</span>;
    if (vulnStatuses.includes(RawVulnStatus.priority)) {
      buttonContent = (
        <span>
          Priority Review <Icon icon={icons.exclamationCircle} />
        </span>
      );
      needsReview = true;
    } else if (vulnStatuses.includes(RawVulnStatus.review)) {
      buttonContent = <span>Review</span>;
      needsReview = true;
    } else if (vulnStatuses.includes(RawVulnStatus.new)) {
      buttonContent = <span>Edit New</span>;
    } else if (vulnStatuses.includes(RawVulnStatus.disabled)) {
      buttonContent = <span>Disabled</span>;
    }

    return (
      <>
        <Button
          type={(isResolved && !needsReview) ? 'error' : 'secondary'}
          size="small"
          onClick={() => {
            setRawVulnsEditIndex(rawVulnsIndexToEdit);
            setShowReviewModal(true);
          }}
        >
          {buttonContent}
        </Button>
        {isResolved && <b style={{ float: 'right', paddingRight: '1em' }}>Resolution Issue</b>}
      </>
    );
  }, [rawVulnData, setRawVulnsEditIndex, setShowReviewModal]);

  const columnMeta: RawVulnColumnMeta[] = [
    {
      displayName: 'Package',
      columnName: 'projectName',
      sortable: true,
    },
    {
      displayName: 'CVE',
      columnName: 'cve',
      sortable: true,
    },
    {
      displayName: 'Fetcher',
      columnName: 'fetcher',
      sortable: true,
      cellFn: (rowInfo: RowInfo) => (rowInfo.row.fetcher !== 'rpm-generic' ? rowInfo.row.fetcher : 'rpm'),
    },
    {
      displayName: 'Created',
      columnName: 'createdAt',
      sortable: true,
      cellFn: (rowInfo: RowInfo) => moment(rowInfo.row.createdAt).format('MM/DD/YY h:mm a'),
    },
    {
      displayName: 'Edit/Review',
      columnName: 'status',
      cellFn: EditReviewButton,
      sortable: true,
    },
    // hidden cols
    {
      displayName: 'rawVulnAuditId',
      columnName: 'rawVulnAuditId',
      show: false,
    },
    {
      displayName: 'Resolved',
      columnName: 'isResolved',
      show: false,
    },
  ];

  const columns: TableProps['columns'] = columnMeta.map((col) => ({
    Header: (
      <div>
        {col.displayName}
        {col.sortable && (
          <span className="pull-right">
            <Icon icon={icons.caretDown} />
            <Icon icon={icons.caretUp} />
          </span>
        )}
      </div>
    ),
    accessor: col.columnName,
    show: col.show ?? true,
    sortable: !!col.sortable,
    Cell: col.cellFn || ((currCol: CellInfo) => currCol.value),
  }));

  const getTrProps: ComponentPropsGetterR = (_state, rowInfo?: RowInfo) => {
    if (!rowInfo) {
      return {};
    }

    const rawVulnsIndexToEdit = getGroupedIndex(
      rowInfo.row.cve,
      rowInfo.row.projectName,
      rowInfo.row.fetcher,
    );
    const rawVulnGroup = rawVulnData[rawVulnsIndexToEdit] || [];
    const doesNeedReview = rawVulnGroup.some(
      (rawVuln: CurrentRawVuln) => rawVuln.status !== RawVulnStatus.complete && rawVuln.status !== RawVulnStatus.new,
    );
    const isDisabled = rawVulnGroup.some((rawVuln: CurrentRawVuln) => rawVuln.status === RawVulnStatus.disabled);
    const isNew = rawVulnGroup.some((rawVuln: CurrentRawVuln) => rawVuln.status === RawVulnStatus.new);
    const isResolved = rowInfo.row?.isResolved;

    let background;
    let fontWeight;
    let fontColor;

    if (isDisabled) {
      background = '#ddd';
      fontColor = '#777';
    } else if (doesNeedReview) {
      background = 'white';
      fontColor = 'rgba(225, 30, 30, 0.8)';
      fontWeight = 'bold';
    } else if (isResolved === false) {
      background = 'rgba(225, 30, 30, 0.8)';
      fontColor = '#fafafa';
    } else if (isNew) {
      background = 'white';
      fontColor = '#ff6b00';
    } else {
      background = '#fafafa';
    }

    return {
      style: {
        background,
        fontWeight: fontWeight || undefined,
        color: fontColor || undefined,
      },
    };
  };

  useEffect(() => {
    setTableData(reduceRawVulnToTableData(rawVulnData));
  }, [rawVulnData]);

  return (
    <ReactTable
      manual
      data={tableData}
      page={page}
      pages={numPages}
      onPageChange={handleChangePage}
      onPageSizeChange={handleChangePageSize}
      onSortedChange={handleChangeSorting}
      columns={columns}
      defaultPageSize={pageSize}
      loading={isLoading}
      minRows={0}
      getTrProps={getTrProps}
    />
  );
};

export default RawVulnsTable;
